import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import type { IUkhoNoticeRow, IUkhoUnmatchedReason } from "interfaces";
import type { IParkWithDateRequest, IReactivateWithDateRequest } from "./api";

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Pure helpers of the UKHO transfer notice screens: the three Vessel Transfers
 * tabs, their dialogs and the vessel page's "UKHO transfers" block. Days are
 * YYYY-MM-DD text (UKHO's days); nothing here reads the clock.
 */

/**
 * What the park and reactivate dialogs need. A work-list row (IUkhoWorkRow)
 * has these fields; the vessel page builds one from its vessel view.
 */
export interface UkhoDialogTarget {
  vesselId: number;
  vesselName: string;
  imoNumber: string;
  /** The vessel's shipco today. */
  customer: string | null;
  /** The shipco the vessel left at its latest park. */
  previousCustomer: string | null;
  previousShipcoId: number | null;
  /** The UKHO day of the notice, YYYY-MM-DD. */
  day: string;
  transferId: string;
}

const MS_PER_DAY = 86_400_000;

const dayPart = (iso: string): string => iso.slice(0, 10);

/** "16 Jul 2026" (no leading zero: "8 Sep 2026"). */
export const formatDay = (iso: string): string =>
  dayjs(dayPart(iso)).format("D MMM YYYY");

/**
 * "16 Jul 2026, 09:14 UK": an instant (ISO 8601 UTC text) on the UK clock,
 * summer time included. Times on these screens are UK time and say so.
 */
export const formatUkDateTime = (iso: string): string =>
  `${dayjs.utc(iso).tz("Europe/London").format("D MMM YYYY, HH:mm")} UK`;

/**
 * Whole days from the day to today's calendar date (0 on the day itself).
 * Both sides count as calendar dates, so the time of day and a summer-time
 * change make no difference.
 */
export const daysAgo = (iso: string, today: Date): number => {
  const [year, month, day] = dayPart(iso).split("-").map(Number);
  const dayAsUtc = Date.UTC(year, month - 1, day);
  const todayAsUtc = Date.UTC(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  return Math.round((todayAsUtc - dayAsUtc) / MS_PER_DAY);
};

const SHORT_SERVICE_NAMES: Record<string, string> = {
  "AVCS Products": "AVCS",
  ADP: "ADP",
  eNP: "eNP",
  "ARCS Charts": "ARCS",
  "Paper Publications": "Paper",
  "Misc Publications": "Misc",
};

/** "AVCS, ADP, eNP"; a name without a short form stays as UKHO prints it. */
export const shortServices = (services: string[]): string =>
  services.map((service) => SHORT_SERVICE_NAMES[service] ?? service).join(", ");

/** The reason a park with the UKHO leaving day is filed with. */
export const parkReason = (day: string, transferId: string): string =>
  `UKHO transfer out on ${formatDay(day)}, transfer ${transferId}`;

/** The reason a reactivation with the UKHO arriving day is filed with. */
export const reactivateReason = (day: string, transferId: string): string =>
  `UKHO transfer in on ${formatDay(day)}, transfer ${transferId}`;

/** POST /vessel-transfer body: park the vessel under Deactivated Vessels. */
export const parkRequest = (
  row: UkhoDialogTarget,
  deactivatedShipcoId: number,
  effectiveDate: string,
  reason: string
): IParkWithDateRequest => ({
  transfer_type: "to_old_vessels",
  vessel_imo_numbers: [row.imoNumber],
  target_shipco_id: deactivatedShipcoId,
  effective_date: effectiveDate,
  reason,
});

/** POST /vessel-transfer body: bring the vessel back to the chosen shipco. */
export const reactivateRequest = (
  row: UkhoDialogTarget,
  targetShipcoId: number,
  effectiveDate: string,
  reason: string
): IReactivateWithDateRequest => ({
  transfer_type: "from_old_vessels",
  vessel_imo_numbers: [row.imoNumber],
  target_shipco_id: targetShipcoId,
  effective_date: effectiveDate,
  reason,
});

/** The chip of a settled work-list row: "parked 20 Jul 2026 by mandar". */
export const handledChipText = (
  action: "parked" | "reactivated",
  handled: { day: string; by: string }
): string => `${action} ${formatDay(handled.day)} by ${handled.by}`;

/** A notice's direction as the screens say it. */
export const noticeKindWord = (kind: "in" | "out"): "arrived" | "left" =>
  kind === "in" ? "arrived" : "left";

/** "<day> · arrived/left · <UKHO ship name> · <UKHO shipping company>". */
export const matchDialogLine = (notice: IUkhoNoticeRow): string =>
  [
    formatDay(notice.noticeDate),
    noticeKindWord(notice.kind),
    notice.licenceName,
    notice.shippingCompany,
  ]
    .filter((part): part is string => Boolean(part))
    .join(" · ");

export type UnmatchedReasonFilter =
  | "all"
  | "not-in-emariner"
  | "no-imo"
  | "licence-without-ship"
  | "sent-back";

/** The reason select of the Unmatched notices tab; four groups over the five reasons. */
export const UNMATCHED_REASON_FILTER_OPTIONS: {
  value: UnmatchedReasonFilter;
  label: string;
}[] = [
  { value: "all", label: "All reasons" },
  { value: "not-in-emariner", label: "Ship not in eMariner" },
  { value: "no-imo", label: "No IMO" },
  { value: "licence-without-ship", label: "Licence without ship" },
  { value: "sent-back", label: "Sent back by hand" },
];

const REASONS_OF_FILTER: Record<
  Exclude<UnmatchedReasonFilter, "all">,
  IUkhoUnmatchedReason[]
> = {
  "not-in-emariner": ["ship not in eMariner", "ship not in eMariner yet"],
  "no-imo": ["no IMO, licence unknown"],
  "licence-without-ship": ["licence found, no ship attached"],
  "sent-back": ["sent back by hand"],
};

/** Whether a row with this reason stays in the list under the chosen filter. */
export const matchesReasonFilter = (
  reason: IUkhoUnmatchedReason,
  filter: UnmatchedReasonFilter
): boolean => filter === "all" || REASONS_OF_FILTER[filter].includes(reason);
